/**
 * Os MARCOS de milhão do total geral, e a comemoração de cada um.
 *
 * Decidido em 13/10, com o evento no ar: a cada R$ 1 milhão que o total da War
 * Room cruza, o telão para tudo, mostra o valor em letras enormes, lista o
 * primeiro representante de cada marca e joga confete.
 *
 * Vive fora do `.tsx` pela mesma razão do `feed.ts` e do `ranking.ts`: o
 * `node --test` remove tipos de `.ts` mas não compila JSX, então o que é
 * testável mora aqui e o componente só desenha.
 */

import type { Divisao } from './api'

/** O passo da comemoração. Um milhão. */
export const PASSO_DO_MARCO = 1_000_000

/**
 * Quanto tempo cada comemoração fica na tela.
 *
 * O teto de 20 s era de 13/10, quando a tela tinha o número, uma frase e o top 3
 * de cada marca. Em 14/10 ela passou a ter também o pódio por marca e o top 15
 * da festa por marca — mais linha para ler de 4 metros —, e os tempos subiram a
 * pedido: a comemoração que ninguém termina de ler vira susto, não festa.
 *
 * Vinte segundos no milhão (são 28 deles no evento, e eles se repetem); trinta
 * na METADE e trinta e cinco na META, que acontecem uma vez cada e são as duas
 * que a sala vai querer fotografar.
 */
export const DURACAO_MS = 20_000
export const DURACAO_META_MS = 35_000
export const DURACAO_METADE_MS = 30_000

/** Onde ficam registradas as comemoração únicas. Ver `CHAVE_ULTIMO_MARCO`. */
export const CHAVE_META_CELEBRADA = 'warroom.metaCelebrada'
export const CHAVE_METADE_CELEBRADA = 'warroom.metadeCelebrada'

/**
 * Onde o último marco celebrado é guardado.
 *
 * Em `localStorage` porque o telão fica dias ligado e a página é recarregada
 * (deploy, F5 de quem está operando, reconexão): sem isso, cada recarga com o
 * total em R$ 6,2 mi comemoraria o sexto milhão outra vez, e a sala aprende a
 * ignorar o confete.
 */
export const CHAVE_ULTIMO_MARCO = 'warroom.ultimoMarcoCelebrado'

/** Em qual marco de milhão este valor está. R$ 6.200.000 → 6. */
export function marcoDe(valor: number | null): number {
  if (typeof valor !== 'number' || !Number.isFinite(valor) || valor < 0) return 0
  return Math.floor(valor / PASSO_DO_MARCO)
}

/**
 * O marco a comemorar, ou `null`.
 *
 * `ultimo` é o que já foi comemorado (de `localStorage`); `null` significa que
 * esta é a PRIMEIRA leitura desta sessão — e aí nada dispara, só se registra
 * onde o total está. Sem essa regra, abrir o telão com R$ 5,4 mi na tela
 * comemoraria o quinto milhão no primeiro segundo, por um marco que foi batido
 * antes de alguém abrir a página.
 *
 * Quando o total pula mais de um milhão entre duas leituras — possível num ciclo
 * de 2 min com pedido grande entrando —, comemora-se o MAIOR e os intermediários
 * ficam para trás. Enfileirar quinze segundos por marco deixaria o telão
 * comemorando passado enquanto o número já andou.
 */
export function marcoACelebrar(total: number | null, ultimo: number | null): number | null {
  const agora = marcoDe(total)
  if (agora <= 0) return null
  if (ultimo === null) return null
  return agora > ultimo ? agora : null
}

/**
 * O total cruzou a METADE da meta?
 *
 * Pedido em 13/10: "quando chegar na metade do valor total da meta, mostre algo
 * também". Com a meta em R$ 28 mi, é R$ 14 mi — e a conta sai da meta, não de um
 * número fixo, para a comemoração acompanhar quem mexer na meta pelo admin.
 *
 * Marco à parte dos milhões, pela mesma razão da meta: metade de R$ 28,5 mi é
 * R$ 14,25 mi, que não cai em travessia de milhão nenhuma.
 */
export function metadeBatida(
  total: number | null,
  metaGeral: number | null,
  jaCelebrada: boolean,
): boolean {
  if (jaCelebrada) return false
  if (typeof total !== 'number' || !Number.isFinite(total)) return false
  if (typeof metaGeral !== 'number' || !Number.isFinite(metaGeral) || metaGeral <= 0) return false
  return total >= metaGeral / 2
}

/**
 * O total cruzou a META GERAL?
 *
 * Tratada como um marco À PARTE dos milhões, e não como "o milhão 28", por uma
 * razão prática: a meta não tem de ser redonda. Hoje ela é R$ 28 mi exatos e os
 * dois coincidem — mas numa meta de R$ 28,5 mi, cruzar o 28º milhão não é bater
 * a meta, e bater a meta não cairia em nenhuma travessia de milhão. Com a
 * verificação separada, os dois casos funcionam, e quando coincidem a META
 * vence: uma comemoração, não duas.
 */
export function metaBatida(
  total: number | null,
  metaGeral: number | null,
  jaCelebrada: boolean,
): boolean {
  if (jaCelebrada) return false
  if (typeof total !== 'number' || !Number.isFinite(total)) return false
  if (typeof metaGeral !== 'number' || !Number.isFinite(metaGeral) || metaGeral <= 0) return false
  return total >= metaGeral
}

/** O que o ensaio pelo endereço pede: a tela da metade, a da meta ou um milhão. */
export type ComemoracaoDeTeste = { tipo: 'metade' | 'meta' | 'milhao'; marco: number }

/**
 * Ensaio da comemoração GERAL pelo endereço: `?comemorar=metade`, `?comemorar=meta`
 * ou `?comemorar=7` (o sétimo milhão).
 *
 * Existe pela mesma razão do `?festa=` das marcas, e por uma a mais: a METADE e
 * a META acontecem **uma vez cada** no evento e ficam registradas em
 * `localStorage`. Depois que o total passa do marco, nem limpar a memória as traz
 * de volta — o gancho ANCORA na primeira leitura e marca como celebrada, senão
 * toda recarga do telão repetiria a festa. Então, para rodar de novo na sala, o
 * caminho é este.
 *
 * Diferente do `?festa=`, aqui a tela **sai sozinha** quando o tempo acaba: o
 * telão volta ao painel sem ninguém mexer no endereço.
 */
export function comemoracaoDeTeste(busca: string): ComemoracaoDeTeste | null {
  let bruto: string | null = null
  try {
    bruto = new URLSearchParams(busca ?? '').get('comemorar')
  } catch {
    return null
  }
  if (bruto === null) return null
  const limpo = bruto.trim().toLowerCase()
  if (limpo === 'metade') return { tipo: 'metade', marco: 0 }
  if (limpo === 'meta') return { tipo: 'meta', marco: 0 }
  const numero = Number.parseInt(limpo, 10)
  /* Só milhão inteiro e positivo: `?comemorar=0` não é marco nenhum, e o texto
     por extenso começa em UM. */
  if (Number.isInteger(numero) && numero >= 1 && numero <= 999) {
    return { tipo: 'milhao', marco: numero }
  }
  return null
}

const UNIDADES = [
  '', 'UM', 'DOIS', 'TRÊS', 'QUATRO', 'CINCO', 'SEIS', 'SETE', 'OITO', 'NOVE', 'DEZ',
  'ONZE', 'DOZE', 'TREZE', 'QUATORZE', 'QUINZE', 'DEZESSEIS', 'DEZESSETE', 'DEZOITO', 'DEZENOVE',
]
const DEZENAS = ['', '', 'VINTE', 'TRINTA', 'QUARENTA', 'CINQUENTA', 'SESSENTA', 'SETENTA', 'OITENTA', 'NOVENTA']

/**
 * O marco por extenso, em maiúsculas: `6` → `SEIS MILHÕES!`.
 *
 * Por extenso e não `6 MILHÕES` porque o número já aparece gigante acima, em
 * reais — repetir o dígito não acrescenta, e a palavra é o que faz a linha ser
 * lida como comemoração. Acima de 99 cai no dígito, que é o limite onde a
 * forma escrita deixaria de caber na tela (a meta geral é R$ 100 mi).
 */
export function marcoPorExtenso(marco: number): string {
  if (!Number.isInteger(marco) || marco < 1) return ''
  const plural = marco === 1 ? 'MILHÃO!' : 'MILHÕES!'
  if (marco >= 100) return `${marco} ${plural}`
  if (marco < 20) return `${UNIDADES[marco]} ${plural}`
  const dezena = Math.floor(marco / 10)
  const unidade = marco % 10
  const escrito = unidade === 0 ? DEZENAS[dezena] : `${DEZENAS[dezena]} E ${UNIDADES[unidade]}`
  return `${escrito} ${plural}`
}

/** Uma linha do pódio: a marca, a posição, o representante e o valor dele. */
export type LinhaDoPodio = {
  id: string
  marca: string
  /** `true` só na primeira linha de cada marca — é ela que leva o nome da marca
   *  na tela; as outras duas aparecem sem repetir o cabeçalho. */
  primeiraDaMarca: boolean
  posicao: number
  nome: string
  valor: number
}

/** Quantos representantes por marca o pódio mostra. */
export const POR_MARCA_NO_PODIO = 3

/**
 * O TOP 3 de cada marca, para o pódio da comemoração.
 *
 * As marcas vêm ordenadas pelo valor do campeão de cada uma, da maior para a
 * menor — quem mais vendeu em cima, que é o que a sala quer ver —, e dentro de
 * cada marca as três linhas vêm na ordem do ranking.
 *
 * Marca sem ranking medido (lista vazia) FICA DE FORA, em vez de aparecer com
 * `—`: a tela é uma comemoração, e linha vazia no meio dela se lê como defeito.
 *
 * O nome exibido prefere a RAZÃO SOCIAL (é o que a coluna do ranking mostra) e
 * cai na fantasia ou no nome da pessoa quando ela falta no cadastro do ERP.
 */
export function podioDasMarcas(
  divisoes: Divisao[],
  /* Quantos por marca. Parâmetro desde 14/10, por causa do MODO PROJETOR: lá
     são 24 linhas em 11 px (o piso do `clamp` em 1024×768), e o telão mostra
     só o campeão de cada marca — 8 linhas que cabem em 34 px. */
  quantos: number = POR_MARCA_NO_PODIO,
): LinhaDoPodio[] {
  const teto = Number.isFinite(quantos) && quantos >= 1 ? Math.trunc(quantos) : POR_MARCA_NO_PODIO
  const porMarca: Array<{ topo: number; linhas: LinhaDoPodio[] }> = []

  for (const divisao of Array.isArray(divisoes) ? divisoes : []) {
    /* `divisao?.` e não `divisao.`: esta função é a última coisa entre o painel
       e um telão em branco. Um item nulo no array — que o tipo diz que não
       existe, e que um erro de montagem do payload produziria — derrubaria a
       comemoração INTEIRA no instante em que ela mais aparece. */
    const tops = Array.isArray(divisao?.topRepresentantes) ? divisao.topRepresentantes : []
    const linhas: LinhaDoPodio[] = []

    for (const item of [...tops].sort((um, outro) => (um?.posicao ?? 0) - (outro?.posicao ?? 0))) {
      if (!item || typeof item.valor !== 'number' || !Number.isFinite(item.valor)) continue
      const nome = texto(item.nome) ?? texto(item.fantasia) ?? texto(item.nomePessoal)
      if (nome === null) continue
      linhas.push({
        id: `${divisao.id}-${item.posicao}`,
        marca: divisao.nome,
        primeiraDaMarca: linhas.length === 0,
        posicao: item.posicao,
        nome,
        valor: item.valor,
      })
      if (linhas.length >= teto) break
    }

    if (linhas.length > 0) porMarca.push({ topo: linhas[0]!.valor, linhas })
  }

  return porMarca.sort((um, outro) => outro.topo - um.topo).flatMap((marca) => marca.linhas)
}

/** Um bloco do podio: a marca e as ate tres linhas dela. */
export type BlocoDoPodio = { id: string; marca: string; linhas: LinhaDoPodio[] }

/**
 * Agrupa o podio em blocos de marca, preservando a ordem.
 *
 * O telao mostra os blocos em DUAS COLUNAS: 24 linhas numa coluna so davam
 * 1.243 px e nao cabiam em 1.080 — medido na captura de 13/10, e projetor nao
 * rola. Agrupar tambem e o que faz os tres de cada marca se lerem juntos.
 */
export function blocosDoPodio(linhas: LinhaDoPodio[]): BlocoDoPodio[] {
  const blocos: BlocoDoPodio[] = []
  for (const linha of Array.isArray(linhas) ? linhas : []) {
    if (linha === null || linha === undefined) continue
    const ultimo = blocos[blocos.length - 1]
    if (linha.primeiraDaMarca || ultimo === undefined) {
      blocos.push({ id: linha.id, marca: linha.marca, linhas: [linha] })
      continue
    }
    ultimo.linhas.push(linha)
  }
  return blocos
}

function texto(valor: string | null | undefined): string | null {
  if (typeof valor !== 'string') return null
  const limpo = valor.trim()
  return limpo === '' ? null : limpo
}

/** A METADE da meta já foi comemorada nesta instalação do telão? */
export function lerMetadeCelebrada(armazem: Pick<Storage, 'getItem'> | null): boolean {
  try {
    return armazem?.getItem(CHAVE_METADE_CELEBRADA) === '1'
  } catch {
    return false
  }
}

/** Registra que a METADE foi comemorada. Falha em silêncio, como as outras. */
export function gravarMetadeCelebrada(armazem: Pick<Storage, 'setItem'> | null): void {
  try {
    armazem?.setItem(CHAVE_METADE_CELEBRADA, '1')
  } catch {
    /* idem */
  }
}

/** A META já foi comemorada nesta instalação do telão? */
export function lerMetaCelebrada(armazem: Pick<Storage, 'getItem'> | null): boolean {
  try {
    return armazem?.getItem(CHAVE_META_CELEBRADA) === '1'
  } catch {
    return false
  }
}

/** Registra que a META foi comemorada. Falha em silêncio, como as outras. */
export function gravarMetaCelebrada(armazem: Pick<Storage, 'setItem'> | null): void {
  try {
    armazem?.setItem(CHAVE_META_CELEBRADA, '1')
  } catch {
    /* idem */
  }
}

/** Lê o último marco celebrado. `null` quando não há nada guardado. */
export function lerUltimoMarco(armazem: Pick<Storage, 'getItem'> | null): number | null {
  try {
    const bruto = armazem?.getItem(CHAVE_ULTIMO_MARCO)
    if (bruto === null || bruto === undefined) return null
    const numero = Number.parseInt(bruto, 10)
    return Number.isInteger(numero) && numero >= 0 ? numero : null
  } catch {
    /* `localStorage` lança em janela com cookies bloqueados e em modo privado de
       alguns navegadores. A comemoração não pode derrubar o telão: sem memória,
       ela simplesmente não repete marco dentro da mesma sessão (o estado em
       memória basta para isso). */
    return null
  }
}

/** Grava o marco celebrado. Falha em silêncio, pela mesma razão acima. */
export function gravarUltimoMarco(armazem: Pick<Storage, 'setItem'> | null, marco: number): void {
  try {
    armazem?.setItem(CHAVE_ULTIMO_MARCO, String(marco))
  } catch {
    /* idem */
  }
}
